import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { getMatchData } from '../../../backend/entryPoints/getMatchDataFromServer';
import { MatchViewModel, MatchViewModelSchema } from '../../../backend/entryPoints/mapper/mapToMatchViewModel';
import { emptyMatchData } from '../../../utils/games';
import {
    extendGamesToMaximumNumberOfGames,
    previousGamesAreValid,
    removeNotNeededGames,
} from '../../../logic/setup/game';
import { saveMatchData } from '../../../backend/entryPoints/saveMatchDataToServer';
import { useTranslation } from 'react-i18next';

function SetupGames(props: { game: string; saveErrorMessage: (msg: string) => void }) {
    const { t } = useTranslation();
    const [match, setMatch] = useState<MatchViewModel>(emptyMatchData);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [games, setGames] = useState<{ home_points: number; guest_points: number }[]>([
        { home_points: 0, guest_points: 0 },
    ]);
    const [intervalRefId, setIntervalRefId] = useState<any>(null);
    let intervalStarted = false;

    const isDoubles = props.game.includes('D');

    useEffect(() => {
        // TODO: Find out why interval is setup twice (It should only be called once)
        // This is a working work-around for the moment
        if (!intervalStarted) {
            intervalStarted = true;
            startIntervalUpdates();
        }
    }, [props.game]);

    const startIntervalUpdates = () => {
        const intervalID = setInterval(() => {
            getMatchData(props.game).then((data) =>
                data.json().then((dataString) => {
                    const model = MatchViewModelSchema.parse(dataString);
                    setGames(model.games);
                    setMatch(model);
                })
            );
        }, 2500);
        setIntervalRefId(intervalID);
    };

    const enableEdit = () => {
        clearInterval(intervalRefId);
        const availableGames = extendGamesToMaximumNumberOfGames(games);
        setGames(availableGames);
        setEditMode(true);
    };

    const updateHomePoints = (index: number, value: number) => {
        const values = games.map((game, idx) => {
            if (idx === index) return { ...game, home_points: value };
            return game;
        });
        setGames(values);
    };

    const updateGuestPoints = (index: number, value: number) => {
        const values = games.map((game, idx) => {
            if (idx === index) return { ...game, guest_points: value };
            return game;
        });
        setGames(values);
    };

    const handleClose = () => {
        setGames(match.games);
        setEditMode(false);
        startIntervalUpdates();
    };

    const saveChanges = () => {
        const remainingGames = removeNotNeededGames(games);
        const sanityCheck = previousGamesAreValid(remainingGames);
        if (sanityCheck.result) {
            const newMatchState = { ...match, games: remainingGames };
            saveMatchData(newMatchState)
                .then(() => console.log('Update match state'))
                .catch((e) => console.error(e));
            setGames(remainingGames);
            setMatch(newMatchState);
            setEditMode(false);
            startIntervalUpdates();
        } else {
            props.saveErrorMessage(sanityCheck.message || '');
        }
    };

    return match ? (
        <Card style={{ margin: '10px', backgroundColor: '#f6f5f5' }}>
            <Card.Body>
                <Card.Title style={{ textAlign: 'center' }}>
                    <Row>
                        <Col></Col>
                        <Col>
                            <span>{match.mode_string}</span>
                        </Col>
                        <Col style={{ textAlign: 'end' }}>
                            <Button
                                style={{ display: editMode ? 'none' : 'inline-block' }}
                                size={'sm'}
                                onClick={enableEdit}
                            >
                                {t('general.edit')}
                            </Button>
                            <Button
                                style={{ display: editMode ? 'inline-block' : 'none' }}
                                size={'sm'}
                                onClick={saveChanges}
                            >
                                {t('general.save')}
                            </Button>
                            <Button
                                style={{ display: editMode ? 'inline-block' : 'none', marginLeft: '5px' }}
                                size={'sm'}
                                onClick={handleClose}
                            >
                                {t('general.cancel')}
                            </Button>
                        </Col>
                    </Row>
                </Card.Title>
                <Card.Body style={{ backgroundColor: '#ffffff', padding: '10px' }}>
                    <Row>
                        <Col style={{ paddingRight: '20px', fontSize: '24px' }} md={4}>
                            {match.player_home[0]}{' '}
                            {isDoubles ? (
                                <>
                                    <br /> {match.player_home[1]}
                                </>
                            ) : null}
                        </Col>
                        {games.map((game, idx) => (
                            <Col md={2} key={`col-home-${game}-${idx}`}>
                                <Form.Control
                                    placeholder={'0'}
                                    type={'number'}
                                    style={{ fontSize: '24px', textAlign: 'center' }}
                                    value={game.home_points}
                                    onChange={(val) => updateHomePoints(idx, Number(val.target.value))}
                                    disabled={!editMode}
                                />
                            </Col>
                        ))}
                    </Row>
                    <Row style={{ marginTop: '5px' }}>
                        <Col style={{ paddingRight: '20px', fontSize: '24px' }} md={4}>
                            {match.player_guest[0]}{' '}
                            {isDoubles ? (
                                <>
                                    <br /> {match.player_guest[1]}
                                </>
                            ) : null}
                        </Col>
                        {games.map((game, idx) => (
                            <Col md={2} key={`col-guest-${game}-${idx}`}>
                                <Form.Control
                                    placeholder={'0'}
                                    type={'number'}
                                    style={{ fontSize: '24px', textAlign: 'center' }}
                                    value={game.guest_points}
                                    onChange={(val) => updateGuestPoints(idx, Number(val.target.value))}
                                    disabled={!editMode}
                                />
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card.Body>
        </Card>
    ) : null;
}

export default SetupGames;
